import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { resetOnLogout } from '~/reducers/common-actions';
import type { RootState } from '~/store';
import type { ApiClient, ClientPreferenceKeys } from '~/api/types';
import { PickupDeliveryModelType } from '~/api/types';

/**
 * The main client state
 */
export type MainClientState = ApiClient | null;

export const mainClientSlice = createSlice({
    name: 'mainClient',
    initialState: null as MainClientState,
    reducers: {
        setMainClient: (
            state: MainClientState,
            action: PayloadAction<MainClientState>
        ): MainClientState => {
            state = action.payload;
            return state;
        },
        resetMainClient: (): MainClientState => {
            return null;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(resetOnLogout, () => {
            return null;
        });
    }
});

export const { setMainClient, resetMainClient } = mainClientSlice.actions;

export const selectMainClient = (state: RootState): MainClientState =>
    state.mainClient;

export const selectMainClientId = (state: RootState) => {
    return state.mainClient?.id;
};

export const selectMainClientPreference =
    (preferenceName: ClientPreferenceKeys) => (state: RootState) =>
        state.mainClient?.preferences?.[preferenceName];

export const selectAllMainClientPreferences = (state: RootState) =>
    state.mainClient?.preferences;

export const selectIsRoutePlannerDisabled = (state: RootState) => {
    const { enableRouteBuilder, enableScheduler } =
        state.mainClient?.preferences || {};
    return !enableScheduler && !enableRouteBuilder;
};

export const selectIsCarpoolingEnabled = (state: RootState) => {
    const { pickupDeliveryModel } = state.mainClient?.preferences || {};
    return pickupDeliveryModel === PickupDeliveryModelType.CARPOOLING;
};

export const selectIsTaxicabEnabled = (state: RootState) => {
    const { pickupDeliveryModel } = state.mainClient?.preferences || {};
    return pickupDeliveryModel === PickupDeliveryModelType.TAXICAB;
};

export const selectZoneBasedRouting = (state: RootState) => {
    return state.mainClient?.preferences.zoneBasedRouting;
};

export const selectCompletedRoutesRequireEndDepot = (state: RootState) => {
    return (
        state.mainClient?.uiPreferences?.completedRoutesRequireEndDepot ?? false
    );
};

export const selectUse24HourFormat = (state: RootState) => {
    return state.mainClient?.uiPreferences?.use24HourFormat ?? false;
};

export default mainClientSlice.reducer;
